import { PharmaciesPageState } from '@actions';
import {
  ChangeDetectionStrategy, Component,
  ElementRef, HostListener, OnDestroy, OnInit,
  Renderer2, ViewChild
} from '@angular/core';
import { AppState } from '@appStates';
import { LocalizationService, ScreenDetectedService, SeoService } from '@core/services';
import { BtnTypeColor, Pages } from '@enums';
import { Store } from '@ngrx/store';
import { FavoriteGoodsApiService } from '../../core/services/api.services/favorite-goods-api.service';
import { ViewedGoodsApiService } from '../../core/services/api.services/viewed-goods-api.service';
import { AlphabeticalService } from 'src/app/core/services/api.services/alphabetical.service';
import { Router } from '@angular/router';
import { JsonLDServiceService } from 'src/app/core/services/seo.services/json-ldservice.service';
import { RestUrlService, TypeUrl } from 'src/app/core/services/api.services/settings/rest-url.service';
import { environment } from '@env';
import { take } from 'rxjs/operators';


@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StartComponent implements OnInit, OnDestroy {
  @ViewChild('topPosition') public topPosition: ElementRef;

  public isVisibleBlockSeoText = false;
  public colors: typeof BtnTypeColor = BtnTypeColor;
  public showScrollBtn = false;
  public lang: string;
  public activeType = 0;
  public cyrillicLatters: string[] = [];
  public latinLatters: string[] = [];

  private listenerFn: () => void;

  @HostListener('scroll', ['$event']) public onScroll(event: Event): void {
    const offsetTop: number = (event.target as HTMLElement).scrollTop;
    this.showScrollBtn = offsetTop > 350;
  }

  constructor(
    private readonly viewedGoodsApiService: ViewedGoodsApiService,
    private readonly favoriteGoodsApiService: FavoriteGoodsApiService,
    private readonly renderer: Renderer2,
    private readonly elementRef: ElementRef,
    private readonly localizationService: LocalizationService,
    private readonly store: Store<AppState>,
    private readonly seo: SeoService,
    private readonly screenDetectedService: ScreenDetectedService,
    private readonly alphabeticalService: AlphabeticalService,
    private readonly router: Router,
    private readonly jsonLdService: JsonLDServiceService,
    private readonly restUrlService: RestUrlService
  ) {
    this.seo.getTegRobots(Pages.start);
    this.seo.updateLinkHreflang();
    this.seo.setStaticTitle('start.page.meta.teg.title');
    this.seo.setStaticDescription('start.page.meta.teg.description');

    this.setSeoCompanyScript();
    this.setSeoWebSiteScript();

    this.listenerFn = this.renderer.listen(this.elementRef.nativeElement, 'click', (event: Event) => {
      if ((event.target as HTMLElement).className === 'faq-btn') {
        const instructionMobile: HTMLElement = document.getElementById('instruction-faq');
        this.scrollHelper(instructionMobile);
      }
      if ((event.target as HTMLElement).className === 'faq-desktop') {
        const instructionPC: HTMLElement = document.getElementById('instruction-desktop');
        this.scrollHelper(instructionPC);
      }
    });
  }

  ngOnInit(): void {
    this.lang = this.localizationService.getLang();
    if (this.screenDetectedService.isDesktop()) {
      this.favoriteGoodsApiService.getProductsFromFavorite();
      this.viewedGoodsApiService.getProductsFromViewed();
    } else {
      this.store.dispatch(PharmaciesPageState({ currentState: null }));
    }
    this.loadAlphabeticalLatters();
  }

  public scrollToTop(): void {
    const topElement: HTMLElement = this.topPosition.nativeElement;
    this.scrollHelper(topElement);
  }

  public switchSeoBlock(): void {
    this.isVisibleBlockSeoText = !this.isVisibleBlockSeoText;
  }

  public setActiveType(type: number): void {
    this.activeType = type;
  }

  public getAlphabeticalLink(): string {
    return this.lang === 'ua' ? '/ua/alphabetical' : '/alphabetical';
  }

  private loadAlphabeticalLatters(): void {
    this.alphabeticalService.getAlphabetical().pipe(
      take(1)
    ).subscribe((response: Array<string[]>) => {
      this.cyrillicLatters = response[0];
      this.latinLatters = response[1];
    });
  }

  private setSeoCompanyScript(): void {
    const logoUrl: string = 'https:' + this.restUrlService.getUrl(TypeUrl.img) + '/logo/logo.svg';
    this.jsonLdService.add(
      JsonLDServiceService.Company(environment.baseUrl, logoUrl),
      'structured-data-company'
    );
  }

  private scrollHelper(element: HTMLElement): void {
    element.scrollIntoView({ behavior: 'smooth' });
  }

  private setSeoWebSiteScript(): void {
    const applicationUrl: string = this.localizationService.getLang() === 'ua' ? `${environment.baseUrl}/ua` : environment.baseUrl;
    this.jsonLdService.add(
      JsonLDServiceService.WebSite(applicationUrl),
      'structured-data-website'
    );
  }

  ngOnDestroy(): void {
    if (this.listenerFn) { this.listenerFn(); }
    this.jsonLdService.remove();
  }
}
